/**
 * API Client for PostgreSQL Backend
 *
 * Handles authenticated requests to the serverless API.
 * Uses Clerk's getToken() to obtain JWT for Authorization header.
 */

import { useAuth } from '@clerk/clerk-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string[]>;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

/**
 * Create authenticated fetch function using Clerk
 */
export function useApiClient() {
  const { getToken } = useAuth();

  async function fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await getToken();

    if (!token) {
      throw new ApiError('Not authenticated', 401);
    }

    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    // Handle no content response
    if (response.status === 204) {
      return undefined as T;
    }

    const json: ApiResponse<T> = await response.json();

    if (!response.ok || !json.success) {
      throw new ApiError(
        json.error || 'Request failed',
        response.status,
        json.errors
      );
    }

    return json.data as T;
  }

  return {
    get: <T>(endpoint: string) => fetchWithAuth<T>(endpoint),

    post: <T>(endpoint: string, data: unknown) =>
      fetchWithAuth<T>(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    patch: <T>(endpoint: string, data: unknown) =>
      fetchWithAuth<T>(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: <T>(endpoint: string) =>
      fetchWithAuth<T>(endpoint, {
        method: 'DELETE',
      }),
  };
}

/**
 * Hook to get paginated response with metadata
 */
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function useApiClientWithMeta() {
  const { getToken } = useAuth();

  async function fetchWithMeta<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<PaginatedResponse<T>> {
    const token = await getToken();

    if (!token) {
      throw new ApiError('Not authenticated', 401);
    }

    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    const json: ApiResponse<T[]> = await response.json();

    if (!response.ok || !json.success) {
      throw new ApiError(
        json.error || 'Request failed',
        response.status,
        json.errors
      );
    }

    return {
      data: json.data || [],
      meta: json.meta || { page: 1, limit: 20, total: 0, totalPages: 0 },
    };
  }

  return {
    get: <T>(endpoint: string) => fetchWithMeta<T>(endpoint),
  };
}
